/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import { parseISO } from "date-fns"
import {
	IconChevronLeftOutline18 as ChevronLeft,
	IconChevronRightOutline18 as ChevronRight,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"
import * as R from "remeda"

import { cn } from "@productlane/lib/src/cn"
import { formatRelativeTime } from "@productlane/lib/src/date"
import {
	Button,
	Checkbox,
	Dialog,
	Empty,
	Input,
	IssueStatusIcon,
	Loader,
	ScrollArea,
	ScrollViewport,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { RouterOutputs } from "@/lib/api"
import EmptyIssue from "@/assets/empty-states/empty-issue.png"
import { api } from "@/lib/api"
import { ProjectSelect } from "../projects/project-select"
import { emptyIssueReason } from "./empty-issue-reason"
import { LinearIssuesError } from "./linear-issues-error"

interface SelectLinearIssuesDialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	showGroupByLabel?: boolean
	status?: "completed"
	excludeStatus?: ("completed" | "canceled")[]
	onSave: (
		issues: RouterOutputs["linear"]["searchIssues"]["nodes"],
		groupByLabel: boolean,
	) => void
	filterImportedIssues?: boolean
	title?: string
	projectId?: string
}

export const SelectLinearIssuesDialog = React.memo(
	({
		isOpen,
		onOpenChange,
		onSave,
		status,
		excludeStatus,
		showGroupByLabel,
		filterImportedIssues = false,
		title = "Select issues",
		projectId: propsProjectId,
	}: SelectLinearIssuesDialogProps) => {
		const [query, setQuery] = useState("")
		const [debouncedQuery, setDebouncedQuery] = useState("")

		const [projects] = useQuery(queries.projects())
		const [importedIssues] = useQuery(queries.issues())

		const [projectId, setProjectId] = useState<string>(propsProjectId ?? "")

		const project = useMemo(() => {
			return projects.find((p) => p.id === projectId)
		}, [projectId, projects])

		const linearProjectId: string = useMemo(() => {
			return projects.find((p) => p.id === projectId)?.linearProjectId ?? ""
		}, [projects, projectId])

		useDebounce(
			() => {
				setDebouncedQuery(query)
			},
			300,
			[query, projectId],
		)

		const [cursors, setCursors] = useState<Array<string>>([])
		const cursor = useMemo(() => {
			return cursors[cursors.length - 1]
		}, [cursors])

		useEffect(() => {
			// Clear cursors when query or projectId changes
			// Add here any other filter that should reset the cursor
			setCursors([])
		}, [query, projectId])

		const getIssuesQuery = api.linear.searchIssues.useQuery(
			{
				query: debouncedQuery,
				projectId: linearProjectId,
				status,
				excludeStatus,
				includeLabels: true,
				after: cursor,
				orderBy: "createdAt",
				excludeImported: filterImportedIssues,
			},
			{
				enabled: isOpen,
				cacheTime: Infinity,
				staleTime: Infinity,
				keepPreviousData: true,
			},
		)

		// TODO: ZERO - Check if this can be done straigh away in the query
		// To sort out already imported issues after importing
		useEffect(() => {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			getIssuesQuery.refetch()
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [importedIssues])

		const emptyReason = useMemo(
			() =>
				emptyIssueReason({
					query: debouncedQuery,
					hidesImported: filterImportedIssues,
					importedCount: importedIssues.length,
				}),
			[debouncedQuery, filterImportedIssues, importedIssues.length],
		)

		const issues = useMemo(() => {
			const issues = getIssuesQuery.data?.nodes

			return R.sortBy(issues ?? [], [
				(issue) => parseISO(issue.completedAt ?? ""),
				"desc",
			])
		}, [getIssuesQuery.data])

		const topRef = useRef<HTMLDivElement>(null)
		useDebounce(
			() => {
				if (typeof window !== "undefined" && topRef.current) {
					topRef.current.scrollIntoView({ behavior: "smooth" })
				}
			},
			100,
			[issues],
		)
		const [selectedIssues, setSelectedIssues] = useState<
			RouterOutputs["linear"]["searchIssues"]["nodes"]
		>([])
		const toggleIssue = useCallback(
			(issue: RouterOutputs["linear"]["searchIssues"]["nodes"][number]) => {
				const foundIdx = selectedIssues.findIndex((ix) => ix.id === issue.id)
				if (foundIdx >= 0) {
					const newIssues = [...selectedIssues]
					newIssues.splice(foundIdx, 1)
					setSelectedIssues(newIssues)
				} else {
					setSelectedIssues([...selectedIssues, issue])
				}
			},
			[selectedIssues],
		)
		const areAllSelected: boolean = useMemo(() => {
			if (issues.length === 0) {
				return false
			}

			return !!issues.every((issue) =>
				selectedIssues.some((selected) => selected?.id === issue?.id),
			)
		}, [issues, selectedIssues])

		const selectAll = useCallback(() => {
			if (!areAllSelected) {
				const newSelectedIssues = [...selectedIssues]
				issues.forEach((issue) => {
					if (!selectedIssues.some((selected) => selected.id === issue.id)) {
						newSelectedIssues.push(issue)
					}
				})
				setSelectedIssues(newSelectedIssues)
			} else {
				const visibleIssueIds = new Set(issues.map((issue) => issue.id))
				const newSelectedIssues = selectedIssues.filter((selected) => {
					const shouldKeep = !visibleIssueIds.has(selected.id)
					return shouldKeep
				})
				setSelectedIssues(newSelectedIssues)
			}
		}, [areAllSelected, issues, selectedIssues])
		const [groupByLabel, setGroupByLabel] = useState(true)

		return (
			<Dialog
				size="lg"
				isOpen={isOpen}
				title={title}
				onOpenChange={onOpenChange}
				footerClassName={cn({
					"sm:justify-between": showGroupByLabel,
					"sm:justify-end": showGroupByLabel,
				})}
				className="overflow-visible px-4"
				header={
					<div className="flex flex-col items-center gap-4 pt-2.5">
						<div
							className="flex w-full items-center justify-between gap-4"
							ref={topRef}
						>
							<Input
								name="search"
								placeholder="Search"
								value={query}
								onChange={(e) => {
									setQuery(e.target.value)
								}}
							/>

							{!propsProjectId && (
								<ProjectSelect
									variant="outline"
									editor={null}
									onChange={(projectId) => {
										setProjectId(projectId ?? "")
									}}
									projectIds={[]}
									side="bottom"
									align="end"
									isHotkeyEnabled={isOpen}
									selectedProject={project}
									disableNewProject={true}
									isPopoverPortal={false}
									modal={false}
								></ProjectSelect>
							)}
						</div>
						<div className="flex w-full items-center gap-3 px-1">
							<Checkbox
								className="text-xs text-secondary hover:text-primary"
								checked={areAllSelected}
								onCheckedChange={() => {
									selectAll()
								}}
							/>
							<button
								type="button"
								className="border-none bg-transparent p-0 text-left align-bottom text-sm font-bold"
								onClick={() => selectAll()}
							>
								Select all
							</button>
						</div>
					</div>
				}
				footer={
					<>
						{showGroupByLabel && (
							<Checkbox
								label="Group issues by label"
								checked={groupByLabel}
								onCheckedChange={(value) => {
									setGroupByLabel(!!value)
								}}
								className="mt-4 sm:mt-0 [&>div]:!gap-1 [&_label]:!ml-1"
							/>
						)}
						<div className="flex w-full flex-col-reverse items-center gap-2 sm:w-auto sm:flex-row">
							<Button
								variant="ghost"
								onClick={() => {
									onOpenChange(false)
								}}
								className="w-full sm:w-auto"
							>
								Cancel
							</Button>
							<Button
								disabled={selectedIssues.length === 0}
								onClick={() => {
									onSave(selectedIssues, groupByLabel)
									onOpenChange(false)
									setSelectedIssues([])
									setCursors([])
								}}
								className="w-full sm:w-auto"
							>
								Import {selectedIssues.length} issues
							</Button>
						</div>
					</>
				}
			>
				{getIssuesQuery.error ? (
					<div className="flex h-[calc(100dvh-25rem)] items-center justify-center">
						<LinearIssuesError
							message={getIssuesQuery.error.message}
							onRetry={() => {
								void getIssuesQuery.refetch()
							}}
							className="max-w-[22rem] pb-2"
						/>
					</div>
				) : getIssuesQuery.isLoading ||
				  getIssuesQuery.isFetching ||
				  !getIssuesQuery.data ? (
					<div className="flex h-[calc(100dvh-25rem)] items-center justify-center gap-2 py-10">
						<Loader />
						<p className="ml-2">Getting Linear issues...</p>
					</div>
				) : issues.length === 0 ? (
					<div className="flex h-[calc(100dvh-25rem)] items-center justify-center">
						<Empty
							text={emptyReason.text}
							title={emptyReason.title}
							action={
								emptyReason.showSettingsLink ? (
									<Link to="/settings/linear">
										<Button variant="outline" size="sm">
											Open Linear settings
										</Button>
									</Link>
								) : undefined
							}
							className="max-w-[20rem] pb-2"
							image={
								<img
									src={EmptyIssue}
									width={79.25}
									height={79.25}
									alt="Empty roadmap"
								/>
							}
						/>
					</div>
				) : (
					<ScrollArea className="-ml-2 h-[calc(100dvh-25rem)]">
						<ScrollViewport>
							<ul className="pr-2">
								{issues?.map((i) => {
									const isSelected = !!selectedIssues.find(
										(ix) => ix.id === i.id,
									)

									return (
										<li
											key={i.id}
											id={i.id}
											className="flex h-10 cursor-default select-none items-center gap-3 rounded-lg px-3 py-0.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-850"
											onClick={() => {
												toggleIssue(i)
											}}
										>
											<Checkbox checked={isSelected ?? false} />
											<IssueStatusIcon
												color={i.state.color ?? ""}
												status={i.state.type}
												className="h-3.5 w-3.5"
											/>
											<p className="w-20 flex-none font-mono tabular-nums text-secondary">
												{i.identifier}
											</p>
											<p className="flex-1 truncate font-medium">{i.title}</p>
											{i.labels && i.labels.nodes.length > 0 && (
												<>
													{/* Mobile: Show dots only */}
													<div className="flex flex-shrink-0 gap-2 sm:hidden">
														{i.labels.nodes.slice(0, 3).map((label) => (
															<div
																key={label.id}
																className="h-2 w-2 flex-shrink-0 rounded-full"
																style={{ backgroundColor: label.color }}
																title={label.name}
															/>
														))}
														{i.labels.nodes.length > 3 && (
															<span
																className="ml-1 text-xs text-secondary"
																title={`+${i.labels.nodes.length - 3} more labels`}
															>
																+{i.labels.nodes.length - 3}
															</span>
														)}
													</div>
													{/* Desktop: Show full badges */}
													<div className="hidden flex-wrap gap-2 sm:flex">
														{i.labels.nodes.length > 2 ? (
															<p className="flex items-center gap-2 rounded-full border px-3 text-secondary">
																{i.labels.nodes.length} Labels
															</p>
														) : (
															i.labels.nodes.map((label) => (
																<div
																	key={label.id}
																	className="flex items-center gap-2 rounded-full border bg-white px-3 py-1 dark:bg-transparent"
																>
																	<div
																		className="h-2 w-2 rounded-full"
																		style={{ backgroundColor: label.color }}
																	/>
																	<p className="">{label.name}</p>
																</div>
															))
														)}
													</div>
												</>
											)}

											{i.completedAt ? (
												<span className="text-secondary">
													{formatRelativeTime(parseISO(i.completedAt))}
												</span>
											) : null}
										</li>
									)
								})}
							</ul>
							{/* Only show pagination buttons if there are multiple pages */}
							{(cursors.length > 0 ||
								getIssuesQuery.data.pageInfo.hasNextPage) && (
								<div className="my-4 flex justify-start gap-2 pl-3">
									<Button
										variant="outline"
										disabled={cursors.length === 0 || getIssuesQuery.isFetching}
										onClick={() => {
											const newCursors = [...cursors]
											newCursors.pop()
											setCursors(newCursors)
										}}
										Icon={ChevronLeft}
										className={cursors.length === 0 ? "opacity-60" : ""}
									>
										Previous
									</Button>
									<Button
										variant="outline"
										disabled={
											!getIssuesQuery.data.pageInfo.hasNextPage ||
											getIssuesQuery.isFetching
										}
										onClick={() => {
											const cursor = getIssuesQuery.data.pageInfo.endCursor
											if (cursor) {
												setCursors([...cursors, cursor])
											}
										}}
										iconRight={ChevronRight}
										className={
											!getIssuesQuery.data.pageInfo.hasNextPage
												? "opacity-60"
												: ""
										}
									>
										Next
									</Button>
								</div>
							)}
						</ScrollViewport>
					</ScrollArea>
				)}
			</Dialog>
		)
	},
)
